import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ShareDialog } from "@/components/ShareDialog";
import { api, apiError } from "@/lib/api";
import { openInvoicePdf } from "@/lib/download";
import { GST_RATES, money, today } from "@/lib/format";
import { CheckCircle, Plus, Trash } from "@phosphor-icons/react";

const blankLine = () => ({ uid: `ln-${Math.random().toString(36).slice(2, 10)}`, item_id: "", item_name: "", hsn: "", qty: 1, rate: 0, discount_pct: 0, gst_rate: 18, uom: "NOS" });

export default function InvoiceEditor() {
  const { kind } = useParams();
  const isSales = kind !== "purchase";
  const navigate = useNavigate();
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [head, setHead] = useState({ party_id: "", date: today(), ref: "", narration: "", generate_einvoice: isSales });
  const [lines, setLines] = useState([blankLine()]);
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/parties?type=${isSales ? "Customer" : "Vendor"}`).then((r) => setParties(r.data));
    api.get("/items").then((r) => setItems(r.data));
  }, [isSales]);

  const party = parties.find((p) => p.id === head.party_id);

  const validLines = useMemo(
    () => lines.filter((l) => l.item_name && Number(l.qty) > 0),
    [lines],
  );

  useEffect(() => {
    if (!validLines.length) { setCalc(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/gst/compute", {
          lines: validLines.map((l) => ({
            item_id: l.item_id || null, item_name: l.item_name, hsn: l.hsn || null,
            qty: Number(l.qty), rate: Number(l.rate), discount_pct: Number(l.discount_pct || 0),
            gst_rate: Number(l.gst_rate), uom: l.uom,
          })),
          party_gstin: party?.gstin || null,
        });
        setCalc(data);
      } catch (e) { console.warn("live GST computation failed", apiError(e)); }
    }, 250);
    return () => clearTimeout(t);
  }, [validLines, party]);

  const setLine = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const pickItem = (i, id) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    setLine(i, { item_id: it.id, item_name: it.name, hsn: it.hsn || "", gst_rate: it.gst_rate, uom: it.uom,
      rate: isSales ? it.sale_price : it.purchase_price });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${isSales ? "sales" : "purchase"}`, {
        party_id: head.party_id, date: head.date, ref: head.ref, narration: head.narration,
        generate_einvoice: isSales && head.generate_einvoice,
        lines: validLines.map((l) => ({
          item_id: l.item_id || null, item_name: l.item_name, hsn: l.hsn || null,
          qty: Number(l.qty), rate: Number(l.rate), discount_pct: Number(l.discount_pct || 0),
          gst_rate: Number(l.gst_rate), uom: l.uom,
        })),
      });
      setPosted(data);
      toast.success(`${isSales ? "Invoice" : "Bill"} ${data.number} posted`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const genIrn = async () => {
    try {
      const { data } = await api.post(`/gst/e-invoice/${posted.id}`);
      setPosted({ ...posted, gst: { ...posted.gst, ...data } });
      toast.success("IRN generated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const pdf = async (mode) => {
    try { await openInvoicePdf(posted.id, posted.number, mode); }
    catch (e) { toast.error(e.message); }
  };

  if (posted) {
    const g = posted.gst || {};
    return (
      <div className="space-y-6" data-testid="invoice-success">
        <PageHeader title={`${posted.type} ${posted.number}`} subtitle={`Posted to the general ledger · ${posted.party_name}`}>
          <Button size="sm" onClick={() => setShareOpen(true)} data-testid="share-invoice-btn">Share</Button>
          <Button size="sm" variant="outline" onClick={() => pdf("print")} data-testid="print-invoice-btn">Print</Button>
          <Button size="sm" variant="outline" onClick={() => pdf("download")} data-testid="download-pdf-btn">Download PDF</Button>
          <Button size="sm" variant="outline" onClick={() => navigate("/vouchers")} data-testid="goto-daybook-btn">Day book</Button>
          <Button size="sm" onClick={() => { setPosted(null); setLines([blankLine()]); setHead({ ...head, party_id: "" }); }}
            data-testid="new-another-btn">New {isSales ? "invoice" : "bill"}</Button>
        </PageHeader>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="panel lg:col-span-2">
            <div className="border-b border-border px-4 py-2.5"><h2 className="font-display text-sm font-bold">Line items</h2></div>
            <table className="dense-table w-full">
              <thead><tr><th>Item</th><th>HSN</th><th className="text-right">Qty</th><th className="text-right">Rate</th>
                <th className="text-right">Taxable</th><th className="text-right">Tax</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {posted.lines.map((l, i) => (
                  <tr key={`${l.item_name}-${i}`}>
                    <td>{l.item_name}</td><td className="num">{l.hsn || "—"}</td>
                    <td className="num text-right">{l.qty}</td><td className="num text-right">{money(l.rate)}</td>
                    <td className="num text-right">{money(l.taxable)}</td>
                    <td className="num text-right">{money(l.cgst + l.sgst + l.igst + l.cess)}</td>
                    <td className="num text-right font-medium">{money(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="grid grid-cols-2 gap-px border-t border-border bg-border sm:grid-cols-4">
              {[["Taxable", posted.totals.taxable], ["CGST", posted.totals.cgst], ["SGST", posted.totals.sgst],
                ["IGST", posted.totals.igst], ["Cess", posted.totals.cess], ["Round off", posted.totals.round_off],
                ["Invoice total", posted.totals.total]].map(([k, v]) => (
                <div key={k} className="bg-background p-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
                  <p className="num font-medium">{money(v)}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="panel" data-testid="einvoice-panel">
            <div className="border-b border-border px-4 py-2.5"><h2 className="font-display text-sm font-bold">e-Invoice status</h2></div>
            <div className="space-y-3 p-4 text-sm">
              <p className="text-xs text-muted-foreground">{g.supply_type} · POS {g.place_of_supply_name || g.place_of_supply}</p>
              {g.irn ? (
                <>
                  <p className="flex items-center gap-1 text-success" data-testid="irn-generated">
                    <CheckCircle size={14} /> IRN generated (simulated NIC)
                  </p>
                  <p className="num break-all text-[11px]">{g.irn}</p>
                  <p className="num text-[11px] text-muted-foreground">Ack {g.ack_no} · {g.ack_date}</p>
                  <div className="border border-border p-2">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Signed QR payload</p>
                    <p className="num mt-1 break-all text-[10px] leading-tight">{(g.qr || "").slice(0, 220)}…</p>
                  </div>
                </>
              ) : (
                <Button size="sm" onClick={genIrn} data-testid="generate-irn-btn">Generate IRN & QR</Button>
              )}
            </div>
          </div>
        </div>
        <ShareDialog voucher={posted} open={shareOpen} onOpenChange={setShareOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoice-editor">
      <PageHeader
        title={isSales ? "Sales invoice" : "Purchase bill"}
        subtitle="Keyboard-first entry · live GST split by place of supply · posts double-entry on save."
      >
        <Button size="sm" onClick={submit} disabled={busy || !head.party_id || !validLines.length} data-testid="post-invoice-btn">
          {busy ? "Posting…" : `Post ${isSales ? "invoice" : "bill"}`}
        </Button>
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="grid gap-3 border border-border p-4 sm:grid-cols-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest">{isSales ? "Customer" : "Vendor"} *</Label>
              <Select value={head.party_id} onValueChange={(v) => setHead({ ...head, party_id: v })}>
                <SelectTrigger data-testid="invoice-party-select"><SelectValue placeholder="Select party" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.gstin ? ` · ${p.gstin}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {party ? (
                <p className="num text-[11px] text-muted-foreground" data-testid="invoice-party-gstin">
                  {party.gstin ? `${party.gstin} · ${party.state}` : "Unregistered (B2C)"}
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest">Date</Label>
              <Input type="date" data-testid="invoice-date-input" value={head.date} onChange={(e) => setHead({ ...head, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest">Reference</Label>
              <Input data-testid="invoice-ref-input" value={head.ref} onChange={(e) => setHead({ ...head, ref: e.target.value })} />
            </div>
          </div>

          <div className="overflow-x-auto border border-border">
            <table className="dense-table w-full min-w-[820px]" data-testid="invoice-lines-table">
              <thead>
                <tr><th className="w-[26%]">Item</th><th>HSN</th><th className="text-right">Qty</th>
                  <th className="text-right">Rate</th><th className="text-right">Disc %</th>
                  <th className="text-right">GST %</th><th className="text-right">Taxable</th><th /></tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const taxable = Number(l.qty || 0) * Number(l.rate || 0) * (1 - Number(l.discount_pct || 0) / 100);
                  return (
                    <tr key={l.uid || `line-${i}`}>
                      <td>
                        <Select value={l.item_id} onValueChange={(v) => pickItem(i, v)}>
                          <SelectTrigger className="h-8 border-0 text-sm" data-testid={`line-item-select-${i}`}>
                            <SelectValue placeholder={l.item_name || "Select item"} />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td><Input className="num h-8 w-20 border-0" data-testid={`line-hsn-input-${i}`} value={l.hsn} onChange={(e) => setLine(i, { hsn: e.target.value })} /></td>
                      <td><Input type="number" className="num h-8 w-16 border-0 text-right" data-testid={`line-qty-input-${i}`} value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></td>
                      <td><Input type="number" className="num h-8 w-24 border-0 text-right" data-testid={`line-rate-input-${i}`} value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} /></td>
                      <td><Input type="number" className="num h-8 w-16 border-0 text-right" data-testid={`line-disc-input-${i}`} value={l.discount_pct} onChange={(e) => setLine(i, { discount_pct: e.target.value })} /></td>
                      <td>
                        <Select value={String(l.gst_rate)} onValueChange={(v) => setLine(i, { gst_rate: v })}>
                          <SelectTrigger className="num h-8 w-20 border-0 text-right" data-testid={`line-gst-select-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="num text-right">{money(taxable)}</td>
                      <td className="text-right">
                        {lines.length > 1 && (
                          <button onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`line-remove-${i}`}
                            className="text-muted-foreground hover:text-destructive"><Trash size={13} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button onClick={() => setLines([...lines, blankLine()])} data-testid="line-add-btn"
              className="flex w-full items-center gap-1 border-t border-border px-3 py-2 text-xs text-primary hover:bg-accent">
              <Plus size={12} /> Add line
            </button>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest">Narration</Label>
            <Input data-testid="invoice-narration-input" value={head.narration} onChange={(e) => setHead({ ...head, narration: e.target.value })} />
          </div>
        </div>

        <div className="space-y-4">
          <div className="panel" data-testid="gst-summary-panel">
            <div className="border-b border-border px-4 py-2.5"><h2 className="font-display text-sm font-bold">Live GST computation</h2></div>
            {calc ? (
              <div className="divide-y divide-border">
                <div className="px-4 py-2 text-xs text-muted-foreground" data-testid="supply-type">
                  {calc.supply_type} · POS {calc.place_of_supply_name || calc.place_of_supply}
                </div>
                {[["Taxable value", calc.totals.taxable], ["CGST", calc.totals.cgst], ["SGST", calc.totals.sgst],
                  ["IGST", calc.totals.igst], ["Cess", calc.totals.cess], ["Round off", calc.totals.round_off]].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between px-4 py-1.5 text-sm">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="num">{money(v)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between bg-muted/40 px-4 py-3">
                  <span className="font-display text-sm font-bold">Invoice total</span>
                  <span className="num text-lg font-semibold" data-testid="invoice-total">{money(calc.totals.total)}</span>
                </div>
              </div>
            ) : (
              <p className="p-4 text-sm text-muted-foreground">Add a line item to see the GST split.</p>
            )}
          </div>

          {isSales && (
            <div className="flex items-center justify-between border border-border p-4">
              <div>
                <p className="text-sm font-medium">Generate e-Invoice (IRN)</p>
                <p className="text-xs text-muted-foreground">Simulated NIC IRP — no live credentials used</p>
              </div>
              <Switch checked={head.generate_einvoice} onCheckedChange={(v) => setHead({ ...head, generate_einvoice: v })}
                data-testid="einvoice-toggle" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
